import { createBrowserHistory, History } from "history";
import {
    applyMiddleware,
    combineReducers,
    createStore,
    Middleware,
    Reducer,
} from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import thunk from "redux-thunk";
import { loggingReducer } from "#store/logging";
import { tablesReducer } from "#store/tables";
import { tableContextReducer } from "#store/tablecontext";
import { environmentReducer } from "#store/environment";
import { localStorageService } from "#services";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { interpretationsReducer } from "#store/interpretations";
import {
    AppDispatch,
    ApplicationState,
    AppStoreType,
    StateType,
} from "#store/types";
import { operatorInvocationsReducer } from "#store/operatorInvocations";
import { actionsReducer } from "#store/actions/actionReducer";
import { uiStateReducer } from "#store/ui";
import { datasourceReducer } from "#store/datasources";
import { autocompleteReducer } from "#store/autocomplete";
import { createReduxHistoryContext } from "redux-first-history";

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<ApplicationState> =
    useSelector;

/**
 * This middleware logs performance marks that are related to redux actions.
 * The results of this can be seen in chrome browser based profiler views.
 * But only in development mode.
 */
const reduxPerformanceMeasuringMiddleware: Middleware<
    {}, // Most middleware do not modify the dispatch return value
    ApplicationState
> = () => next => action => {
    if (
        process.env.NODE_ENV === "development" ||
        performance.mark === undefined
    ) {
        return next(action);
    }
    performance.mark(`${action.type}_start`);
    const result = next(action);
    performance.mark(`${action.type}_end`);
    performance.measure(
        `${action.type}`,
        `${action.type}_start`,
        `${action.type}_end`
    );
};

export class StoreService {
    readonly _store: AppStoreType;
    readonly _history: History;

    constructor() {
        const history = createBrowserHistory();
        const { createReduxHistory, routerMiddleware, routerReducer } =
            createReduxHistoryContext({
                history,
                //other options if needed
            });

        // Make sure that redux-thunks types are exposed from the store.
        const composeEnhancers = composeWithDevTools({
            trace: true,
            traceLimit: 25,
        });
        const store = createStore(
            // Setup root reducer from split reducer functions

            combineReducers<ApplicationState>({
                actions: actionsReducer,
                environment: environmentReducer,
                interpretations: interpretationsReducer,
                logging: loggingReducer,
                operatorInvocations: operatorInvocationsReducer,
                tables: tablesReducer,
                uiState: uiStateReducer,
                tableContext: tableContextReducer,
                autocomplete: autocompleteReducer,
                datasources: datasourceReducer,
                router: routerReducer,
            }),
            /* preloadedState */
            composeEnhancers(
                applyMiddleware<AppDispatch, StateType>(
                    routerMiddleware,
                    thunk,
                    reduxPerformanceMeasuringMiddleware
                )
            )
        );

        this.currentUiState = store.getState().uiState;
        store.subscribe(() => {
            const nextState = store.getState().uiState;
            if (nextState !== this.currentUiState) {
                this.currentUiState = nextState;
                localStorageService.write(nextState);
            }
        });

        this._store = store;
        this._history = createReduxHistory(this.store);
    }

    private currentUiState = localStorageService.read();

    get store() {
        return this._store;
    }

    get history() {
        return this._history;
    }
}
